import React from 'react';
import AuthChooser from '../auth/AuthChooser';
import JGLocalOpsMark from '../common/JGLocalOpsMark';
import LocalOpsActionCreditAudioMoment from './LocalOpsActionCreditAudioMoment';

const normalizeActionLabel = (value) => String(value || '').trim();
const normalizeActionKind = (value) => String(value || '').trim().toLowerCase();
const normalizeActionToken = (value) => normalizeActionLabel(value).toLowerCase();

const ACTION_FAMILIES = {
  TASK: 'task',
  OFFERING: 'offering',
  USE_LOCALLY: 'use_locally',
  AUTH: 'auth',
  UNKNOWN: 'unknown',
};

const humanizeCounterpartyName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const withSpaces = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return withSpaces
    .split(' ')
    .filter(Boolean)
    .map((token) => {
      if (!/[a-z]/i.test(token)) return token;
      return `${token.charAt(0).toUpperCase()}${token.slice(1)}`;
    })
    .join(' ');
};

const isUseLocallyLabel = (value) => {
  const raw = normalizeActionToken(value);
  return (
    /^use jgk locally$/.test(raw) ||
    /^use jgk locally again$/.test(raw) ||
    /^find nearby offers$/.test(raw) ||
    /^open nearby offers$/.test(raw) ||
    /^spend jgk on local offers$/.test(raw) ||
    /^review nearby trusted places that accept jgk now$/.test(raw)
  );
};

const isTaskFamilyLabel = (value) => {
  const raw = normalizeActionToken(value);
  return (
    /^resume accepted task/.test(raw) ||
    /^continue to active task$/.test(raw) ||
    /^resume active task$/.test(raw) ||
    /^resume task$/.test(raw) ||
    /^find another task$/.test(raw) ||
    /^earn again$/.test(raw) ||
    /^create another task$/.test(raw) ||
    /^create next task$/.test(raw) ||
    /^continue earning with nearby high-value localops opportunities$/.test(raw) ||
    /^earn .* today$/.test(raw)
  );
};

const isOfferingFamilyLabel = (value) => {
  const raw = normalizeActionToken(value);
  return (
    /^resume reserved offering/.test(raw) ||
    /^continue to reserved offering$/.test(raw) ||
    /^post another offering$/.test(raw) ||
    /^create another offer$/.test(raw)
  );
};

const inferActionFamily = ({ kind, label }) => {
  const normalizedKind = normalizeActionKind(kind);

  if (
    normalizedKind === 'require_auth' ||
    normalizedKind.includes('auth_required') ||
    normalizedKind.includes('require_auth')
  ) {
    return ACTION_FAMILIES.AUTH;
  }
  if (
    normalizedKind === 'use_locally' ||
    normalizedKind.includes('use_locally') ||
    normalizedKind.includes('use-local')
  ) {
    return ACTION_FAMILIES.USE_LOCALLY;
  }
  if (normalizedKind.includes('offering')) return ACTION_FAMILIES.OFFERING;
  if (normalizedKind.includes('task')) return ACTION_FAMILIES.TASK;

  if (isUseLocallyLabel(label)) return ACTION_FAMILIES.USE_LOCALLY;
  if (isOfferingFamilyLabel(label)) return ACTION_FAMILIES.OFFERING;
  if (isTaskFamilyLabel(label)) return ACTION_FAMILIES.TASK;
  if (/^sign in to continue$/i.test(normalizeActionLabel(label))) {
    return ACTION_FAMILIES.AUTH;
  }
  return ACTION_FAMILIES.UNKNOWN;
};

const buildActionIdentity = ({ family, kind, rawLabel }) => {
  const normalizedLabel = normalizeActionToken(rawLabel);
  if (family === ACTION_FAMILIES.USE_LOCALLY) return 'use_locally:default';
  if (family === ACTION_FAMILIES.AUTH) return 'auth:signin';
  if (family === ACTION_FAMILIES.TASK) {
    if (/^create another task$/.test(normalizedLabel) || /^create next task$/.test(normalizedLabel)) {
      return 'task:create';
    }
    if (/^find another task$/.test(normalizedLabel) || /^earn .* today$/.test(normalizedLabel)) {
      return 'task:discover';
    }
    if (/^earn again$/.test(normalizedLabel)) return 'task:discover';
    return 'task:resume';
  }
  if (family === ACTION_FAMILIES.OFFERING) {
    if (
      /^create another offer$/.test(normalizedLabel) ||
      /^post another offering$/.test(normalizedLabel)
    ) {
      return 'offering:create';
    }
    return 'offering:resume';
  }
  return `${normalizeActionKind(kind) || 'unknown'}:${normalizedLabel || 'none'}`;
};

const resolveCompletionActionPresentation = ({
  action = null,
  fallback,
  isSecondary = false,
}) => {
  const rawLabel = normalizeActionLabel(action?.label);
  const family = inferActionFamily({
    kind: action?.kind,
    label: rawLabel,
  });

  if (family === ACTION_FAMILIES.USE_LOCALLY) {
    return {
      family,
      kind: normalizeActionKind(action?.kind),
      rawLabel,
      label: 'Use JGK locally',
    };
  }

  if (family === ACTION_FAMILIES.AUTH) {
    return {
      family,
      kind: normalizeActionKind(action?.kind),
      rawLabel,
      label: 'Sign in to continue',
    };
  }

  if (family === ACTION_FAMILIES.TASK) {
    if (isSecondary) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: '',
      };
    }
    if (
      /^resume accepted task$/i.test(rawLabel) ||
      /^continue to active task$/i.test(rawLabel) ||
      /^resume active task$/i.test(rawLabel) ||
      /^resume task$/i.test(rawLabel)
    ) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Resume task',
      };
    }
    if (/^find another task$/i.test(rawLabel)) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Find another task',
      };
    }
    if (
      /^earn again$/i.test(rawLabel) ||
      /^continue earning with nearby high-value localops opportunities$/i.test(rawLabel) ||
      /^earn .* today$/i.test(rawLabel)
    ) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Find another task',
      };
    }
    if (/^create another task$/i.test(rawLabel) || /^create next task$/i.test(rawLabel)) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Create another task',
      };
    }
    return {
      family,
      kind: normalizeActionKind(action?.kind),
      rawLabel,
      label: rawLabel || fallback,
    };
  }

  if (family === ACTION_FAMILIES.OFFERING) {
    if (isSecondary) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: '',
      };
    }
    if (/^continue to reserved offering$/i.test(rawLabel)) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Resume reserved offering',
      };
    }
    if (/^post another offering$/i.test(rawLabel) || /^create another offer$/i.test(rawLabel)) {
      return {
        family,
        kind: normalizeActionKind(action?.kind),
        rawLabel,
        label: 'Create another offer',
      };
    }
    return {
      family,
      kind: normalizeActionKind(action?.kind),
      rawLabel,
      label: rawLabel || fallback,
    };
  }

  if (/^continue$/i.test(rawLabel)) {
    return {
      family,
      kind: normalizeActionKind(action?.kind),
      rawLabel,
      label: '',
    };
  }

  return {
    family,
    kind: normalizeActionKind(action?.kind),
    rawLabel,
    label: rawLabel || fallback,
  };
};

// PostCompletionScreen renders completion truth from PlayerMap/backend contract.
// It is UI-only: no side effects, no routing, no fetching.
export default function PostCompletionScreen({
  open = true,
  context,
  completionContext,
  onClose,
  onCashOut,
  onUseLocally,
  onShare,
  onDoAnother,
  onCreateNext,
  onPrimaryAction,
  onSecondaryAction,
  authProps = null,
  playerBalance = null,
  actionCreditAudioEnabled,
  actionCreditAudioAutoPlay = true,
}) {
  const ctx = completionContext || context;
  if (!open || !ctx) return null;

  const {
    kind,
    entityType,
    role = 'tasker',
    paymentMode,
    communityTask,
    amountUSD,
    amountJGK,
    title,
    counterpartyName,
  } = ctx;

  const header = (() => {
    if (title) return title;
    if (kind === 'offering_fulfilled') return 'Offering fulfilled';
    if (entityType === 'offering') return 'Offering completed';
    return 'Task completed';
  })();

  const roleLabel = (() => {
    if (role === 'creator') return 'Creator';
    if (role === 'buyer') return 'Buyer';
    if (role === 'provider') return 'Provider';
    if (role === 'tasker') return 'Local contributor';
    return 'Contributor';
  })();

  const localValue = ctx?.localValueCreated || {};
  const resolvedAmountUSD = Number.isFinite(Number(amountUSD))
    ? Number(amountUSD)
    : Number.isFinite(Number(localValue.amountUSD))
    ? Number(localValue.amountUSD)
    : null;
  const resolvedAmountJGK = Number.isFinite(Number(amountJGK))
    ? Math.round(Number(amountJGK))
    : Number.isFinite(Number(localValue.amountJGK))
    ? Math.round(Number(localValue.amountJGK))
    : Number.isFinite(Number(resolvedAmountUSD))
    ? Math.round(Number(resolvedAmountUSD) * 1000)
    : null;

  const proofLineUSD =
    Number.isFinite(Number(resolvedAmountUSD)) && resolvedAmountUSD !== null
      ? `$${Number(resolvedAmountUSD).toFixed(2)}`
      : null;
  const proofLineJGK =
    Number.isFinite(Number(resolvedAmountJGK)) && resolvedAmountJGK !== null
      ? `${Number(resolvedAmountJGK)} JGK`
      : null;

  const entityLabel = entityType === 'offering' ? 'Offering' : 'Task';
  const compactEntityLabel = entityType === 'offering' ? 'Offer' : 'Task';
  const communityTaskCompletion =
    communityTask === true ||
    paymentMode === 'community' ||
    ctx?.sharePayload?.paymentMode === 'community';
  const displayCounterpartyName = humanizeCounterpartyName(counterpartyName);
  const outcomeSubline = counterpartyName
    ? `Verified with ${displayCounterpartyName}`
    : 'Verified completion';
  const outcomeMetaLine = `${compactEntityLabel} - ${roleLabel}`;

  const actionCredit = ctx?.actionCredit || {};
  const actionCreditAwarded = actionCredit?.awarded === true;
  const actionCreditDuplicate = actionCredit?.duplicate === true;
  const actionCreditEligible =
    actionCredit?.eligibleForViewer === true
      ? true
      : actionCredit?.eligibleForViewer === false
      ? false
      : null;
  const isViewerEarner = actionCreditEligible === true;
  const isOfferingBuyerCompletion =
    kind === 'offering_fulfilled' &&
    entityType === 'offering' &&
    role === 'buyer';

  const actionCreditTotal = Number.isFinite(Number(actionCredit?.total))
    ? Number(actionCredit.total)
    : Number.isFinite(Number(ctx?.trust?.total))
    ? Number(ctx.trust.total)
    : null;

  const trustLabel =
    actionCredit?.trustLabel ||
    ctx?.trust?.label ||
    (Number(actionCreditTotal || 0) >= 1 ? 'Verified Local' : null);
  const trustUnlocked =
    actionCredit?.trustUnlocked === true || ctx?.trust?.unlocked === true;
  const isFirstUnlockMilestone =
    actionCreditAwarded &&
    trustUnlocked &&
    (!Number.isFinite(Number(actionCreditTotal)) || Number(actionCreditTotal) <= 1);
  const outcomeKicker = communityTaskCompletion
    ? 'Community Task verified'
    : actionCreditAwarded
    ? 'Action Credit earned'
    : trustUnlocked
    ? 'Verified milestone'
    : 'Verified completion';

  const hasRewardAmount =
    Number.isFinite(Number(resolvedAmountUSD)) &&
    Number.isFinite(Number(resolvedAmountJGK));
  const zeroRewardState =
    communityTaskCompletion ||
    (hasRewardAmount &&
      Number(resolvedAmountUSD) <= 0 &&
      Number(resolvedAmountJGK) <= 0);

  const actionCreditHeadline = (() => {
    if (actionCreditAwarded) return '+1 Action Credit';
    if (actionCreditDuplicate) return 'Action Credit unchanged';
    if (actionCreditEligible === false) {
      return 'Verified by you';
    }
    if (actionCreditEligible === true) return 'Action Credit syncing';
    return 'Action Credit status';
  })();

  const actionCreditDetailLine = (() => {
    if (actionCreditAwarded && isFirstUnlockMilestone) {
      return 'Verified Local is now unlocked on your local trust record.';
    }
    if (actionCreditAwarded) {
      return 'This verified action is saved to your Action Credit record.';
    }
    if (actionCreditDuplicate) {
      return 'This action was already credited; your verified record was confirmed.';
    }
    if (actionCreditEligible === false) {
      return 'You verified the completion. The contributor receives Action Credit.';
    }
    if (actionCreditEligible === true) {
      return 'Completion verified; credit is syncing now.';
    }
    return 'Completion verified; credit status unavailable.';
  })();

  const trustStatusLine = (() => {
    if (trustLabel && isFirstUnlockMilestone) {
      return `${trustLabel} unlocked`;
    }
    if (trustLabel) return trustLabel;
    return 'Not unlocked yet';
  })();

  const actionCreditStatusLine = Number.isFinite(Number(actionCreditTotal))
    ? `Action Credit: ${actionCreditTotal}`
    : null;
  const actionCreditPill = (() => {
    if (actionCreditAwarded) return 'Awarded';
    if (actionCreditDuplicate) return 'Already credited';
    if (actionCreditEligible === false) return 'Verifier';
    if (actionCreditEligible === true) return 'Syncing';
    return 'Verified';
  })();
  const localActivityReward =
    ctx?.localActivityReward?.awarded === true ? ctx.localActivityReward : null;
  const localActivityRewardAmount = Number(localActivityReward?.amountJGK);
  const localActivityRewardLabel =
    Number.isFinite(localActivityRewardAmount) && localActivityRewardAmount > 0
      ? `${Math.round(localActivityRewardAmount).toLocaleString('en-US')} JGK`
      : null;
  const localActivityRewardCard = localActivityReward ? (
    <div
      data-testid="post-completion-local-activity-reward"
      style={{
        marginBottom: 16,
        padding: 16,
        borderRadius: 14,
        border: '1px solid rgba(45,212,191,0.32)',
        background:
          'linear-gradient(135deg, rgba(20,184,166,0.16), rgba(14,165,233,0.1))',
      }}
    >
      <div style={{ fontSize: 15, color: '#99f6e4', marginBottom: 6 }}>
        {localActivityReward.title || 'Local spark earned'}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 900,
          color: '#f8fafc',
          marginBottom: 6,
        }}
      >
        {localActivityRewardLabel
          ? `You earned ${localActivityRewardLabel}`
          : 'You earned a local activity reward'}
      </div>
      <div
        style={{
          fontSize: 13,
          color: '#cbd5e1',
          marginBottom: localActivityReward.recognitionLabel ? 8 : 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {localActivityReward.message ||
          'Thanks for helping get things moving nearby.'}
      </div>
      {localActivityReward.recognitionLabel ? (
        <div
          style={{
            display: 'inline-flex',
            padding: '5px 10px',
            borderRadius: 999,
            background: 'rgba(45,212,191,0.14)',
            color: '#99f6e4',
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {localActivityReward.recognitionLabel}
        </div>
      ) : null}
    </div>
  ) : null;

  const verifiedRecordLine = (() => {
    if (actionCreditAwarded) {
      return 'Saved to your LocalOps trust record.';
    }
    if (actionCreditDuplicate) {
      return 'Verified record confirmed.';
    }
    if (actionCreditEligible === false) {
      return 'Verified record updated with your confirmation.';
    }
    return 'Verified record updated.';
  })();

  const rewardHeadline = (() => {
    if (communityTaskCompletion) return 'Verified contribution';
    if (zeroRewardState) return 'Verified contribution';
    return proofLineUSD || 'Reward pending';
  })();

  const rewardCardLabel = (() => {
    if (communityTaskCompletion) return 'Community contribution';
    if (zeroRewardState) return 'Verified completion value';
    if (isOfferingBuyerCompletion) return 'Purchase amount';
    if (isViewerEarner) return 'Compensation';
    return 'Completion value';
  })();

  const rewardSubline = (() => {
    if (communityTaskCompletion) {
      return 'No cash payout attached to this Community Task';
    }
    if (zeroRewardState) return 'No payout attached to this completion';
    if (isOfferingBuyerCompletion && proofLineJGK) {
      return `${proofLineJGK} order value`;
    }
    if (isViewerEarner && proofLineJGK) {
      return `${proofLineJGK} released as JGK compensation`;
    }
    return proofLineJGK || 'JGK amount unavailable';
  })();

  const contributionLine = (() => {
    if (communityTaskCompletion) {
      return 'Verified completion strengthens Action Credit.';
    }
    if (zeroRewardState) {
      return 'Verified completion still counts even when no payout is attached.';
    }
    if (isOfferingBuyerCompletion) {
      return 'Purchase confirmed and seller payout released from escrow.';
    }
    return 'Verified completion strengthens your trusted local record.';
  })();

  const walletLine =
    Number.isFinite(Number(playerBalance)) && Number(playerBalance) > 0
      ? `Balance: ${Number(playerBalance).toLocaleString('en-US')} JGK ($${(
          Number(playerBalance) / 1000
        ).toFixed(2)})`
      : null;

  const resolvedSharePayload =
    ctx?.sharePayload ||
    {
      entityId: ctx?.entityId || ctx?.taskId || ctx?.id,
      entityType: ctx?.entityType || 'task',
      amountUSD: resolvedAmountUSD ?? null,
      amountJGK: resolvedAmountJGK ?? null,
    };
  const shareEntityTypeToken = normalizeActionToken(resolvedSharePayload?.entityType);
  const shareEntityType =
    shareEntityTypeToken === 'task' || shareEntityTypeToken === 'offering'
      ? shareEntityTypeToken
      : null;
  const shareEntityIdRaw =
    resolvedSharePayload?.entityId ||
    resolvedSharePayload?.id ||
    (shareEntityType === 'offering'
      ? resolvedSharePayload?.offeringId
      : resolvedSharePayload?.taskId) ||
    null;
  const shareEntityId = String(shareEntityIdRaw || '').trim();
  const sharePayloadIsValid =
    !!shareEntityType &&
    shareEntityId.length > 0;
  const canRenderShare = typeof onShare === 'function' && sharePayloadIsValid;

  const shareHandler = () => {
    if (!canRenderShare) return;
    onShare({
      ...resolvedSharePayload,
      entityType: shareEntityType,
      entityId: shareEntityId,
    });
  };

  const renderButton = (label, handler, variant = 'primary') => {
    if (!handler || !label) return null;
    const styles = {
      primary: {
        background: 'linear-gradient(90deg, #22d3ee, #06b6d4)',
        color: '#0b1220',
      },
      ghost: {
        background: 'rgba(148,163,184,0.12)',
        color: '#e5e7eb',
        border: '1px solid rgba(148,163,184,0.18)',
      },
    }[variant];
    return (
      <button
        type="button"
        onClick={handler}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: 10,
          border: 'none',
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'normal',
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          ...styles,
        }}
      >
        {label}
      </button>
    );
  };

  const renderActions = () => {
    if (ctx?.authRequired && authProps) {
      return (
        <AuthChooser
          headerText={authProps.headerText || 'Sign in to continue'}
          subText={authProps.subText}
          onPrimary={authProps.onPrimary}
          onPrimaryStart={authProps.onPrimaryStart}
          primaryDisabled={authProps.primaryDisabled}
          email={authProps.email}
          onEmailChange={authProps.onEmailChange}
          emailError={authProps.emailError}
          onEmailErrorClear={authProps.onEmailErrorClear}
          onSendMagicLink={authProps.onSendMagicLink}
          magicStatus={authProps.magicStatus}
        />
      );
    }

    const primaryPresentation = resolveCompletionActionPresentation({
      action: ctx?.nextAction,
      fallback: 'Find next local action',
      isSecondary: false,
    });
    const primaryHandler = onPrimaryAction || onDoAnother || onCreateNext || null;
    const secondaryPresentation = resolveCompletionActionPresentation({
      action: ctx?.secondaryAction,
      fallback: 'Use JGK locally',
      isSecondary: true,
    });
    const secondaryHandler = onSecondaryAction || onUseLocally || onCashOut || null;
    const showPrimary = !!(primaryHandler && primaryPresentation.label);
    const primaryIdentity = showPrimary
      ? buildActionIdentity({
          family: primaryPresentation.family,
          kind: primaryPresentation.kind,
          rawLabel: primaryPresentation.rawLabel || primaryPresentation.label,
        })
      : null;

    const secondaryFamilyAllowed =
      secondaryPresentation.family === ACTION_FAMILIES.USE_LOCALLY;
    const showSecondaryCandidate = !!(
      secondaryHandler &&
      secondaryPresentation.label &&
      secondaryFamilyAllowed
    );
    const secondaryIdentity = showSecondaryCandidate
      ? buildActionIdentity({
          family: secondaryPresentation.family,
          kind: secondaryPresentation.kind,
          rawLabel: secondaryPresentation.rawLabel || secondaryPresentation.label,
        })
      : null;
    const showSecondary =
      showSecondaryCandidate && (!showPrimary || primaryIdentity !== secondaryIdentity);

    return (
      <>
        {showPrimary
          ? renderButton(primaryPresentation.label, primaryHandler, 'primary')
          : null}
        {showSecondary
          ? renderButton(
              secondaryPresentation.label,
              secondaryHandler,
              showPrimary ? 'ghost' : 'primary',
            )
          : null}
        {canRenderShare ? renderButton('Share', shareHandler, 'ghost') : null}
      </>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,12,20,0.82)',
        backdropFilter: 'blur(10px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        padding: 'clamp(12px, 3vh, 20px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={header}
        data-testid="post-completion-modal"
        style={{
          width: '100%',
          maxWidth: 540,
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          position: 'relative',
          background: '#0f172a',
          border: '1px solid rgba(148,163,184,0.18)',
          boxShadow: '0 22px 50px rgba(0,0,0,0.45)',
          borderRadius: 18,
          padding: 'clamp(18px, 4vw, 28px)',
          color: '#e5e7eb',
          textAlign: 'left',
        }}
      >
        {onClose ? (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 30,
              height: 30,
              borderRadius: 999,
              border: '1px solid rgba(148,163,184,0.22)',
              background: 'rgba(15,23,42,0.72)',
              color: '#94a3b8',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            <span aria-hidden="true">X</span>
          </button>
        ) : null}
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: '#93c5fd',
            marginBottom: 8,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {outcomeKicker}
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            marginBottom: 8,
            lineHeight: 1.25,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {header}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#9fb6d1',
            marginBottom: 8,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {outcomeSubline}
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#94a3b8',
            marginBottom: 14,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {outcomeMetaLine}
        </div>

        {localActivityRewardCard}

        <div
          style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 14,
            background: 'rgba(148,163,184,0.08)',
            border: '1px solid rgba(148,163,184,0.18)',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 13, color: '#a5b4fc', marginBottom: 5 }}>
            {rewardCardLabel}
          </div>
          <div
            style={{
              fontSize: zeroRewardState ? 21 : 26,
              fontWeight: 800,
              marginBottom: 5,
              lineHeight: 1.1,
              color: zeroRewardState ? '#cbd5e1' : '#e2e8f0',
            }}
          >
            {rewardHeadline}
          </div>
          <div
            style={{
              fontSize: 13,
              color: '#94a3b8',
              marginBottom: zeroRewardState ? 8 : 0,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {rewardSubline}
          </div>
          {zeroRewardState && !communityTaskCompletion ? (
            <div
              style={{
                fontSize: 13,
                color: '#94a3b8',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {`Recorded payout: ${proofLineUSD || '$0.00'} (${proofLineJGK || '0 JGK'})`}
            </div>
          ) : null}
        </div>

        <div
          style={{
            marginBottom: 12,
            padding: 14,
            borderRadius: 14,
            border: actionCreditAwarded
              ? '1px solid rgba(250,204,21,0.38)'
              : '1px solid rgba(96,165,250,0.25)',
            background: actionCreditAwarded
              ? 'linear-gradient(135deg, rgba(14,165,233,0.2), rgba(250,204,21,0.12))'
              : 'rgba(37,99,235,0.12)',
            boxShadow: actionCreditAwarded
              ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 12px 32px rgba(8,47,73,0.22)'
              : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                minWidth: 0,
                flex: '1 1 auto',
                fontSize: 12,
                fontWeight: 900,
                color: actionCreditAwarded ? '#fde68a' : '#bfdbfe',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              <JGLocalOpsMark
                className="post-completion-action-credit-mark"
                label="JG Action Credit System"
              />
            </div>
            <div
              style={{
                flex: '0 0 auto',
                padding: '5px 9px',
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(15,23,42,0.58)',
                color: actionCreditAwarded ? '#fef3c7' : '#dbeafe',
                fontSize: 11,
                fontWeight: 900,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {actionCreditPill}
            </div>
          </div>
          <div
            style={{
              fontSize: actionCreditAwarded ? 26 : 18,
              lineHeight: 1.05,
              fontWeight: 900,
              color: '#f8fafc',
              marginBottom: 6,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {actionCreditHeadline}
          </div>
          <div
            style={{
              fontSize: 14,
              color: actionCreditAwarded ? '#e0f2fe' : '#cbd5e1',
              marginBottom: 8,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            <div>{trustStatusLine}</div>
            {actionCreditStatusLine ? (
              <div style={{ marginTop: 3, fontWeight: 800 }}>
                {actionCreditStatusLine}
              </div>
            ) : null}
          </div>
          {isFirstUnlockMilestone ? (
            <div
              style={{
                marginBottom: 8,
                padding: '6px 9px',
                borderRadius: 10,
                border: '1px solid rgba(251,191,36,0.45)',
                background: 'rgba(245,158,11,0.16)',
                color: '#fde68a',
                fontSize: 13,
                fontWeight: 700,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              Verified Local unlocked with your first credited completion.
            </div>
          ) : null}
          <div
            style={{
              fontSize: 13,
              color: '#cbd5e1',
              marginBottom: 6,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {actionCreditDetailLine}
          </div>
          <div
            style={{
              fontSize: 13,
              color: '#cbd5e1',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {verifiedRecordLine}
          </div>
          <LocalOpsActionCreditAudioMoment
            enabled={actionCreditAudioEnabled}
            autoPlay={actionCreditAudioAutoPlay}
            actionCreditAwarded={actionCreditAwarded}
            actionCreditEligibleForViewer={isViewerEarner}
          />
        </div>

        <div
          style={{
            fontSize: 13,
            fontWeight: 400,
            color: '#9fb6d1',
            marginBottom: 8,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {contributionLine}
        </div>
        {walletLine ? (
          <div
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: '#9fb6d1',
              marginBottom: 12,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {walletLine}
          </div>
        ) : null}

        <div style={{ fontSize: 14, color: '#cbd5e1', marginBottom: 6 }}>Next step</div>
        <div style={{ display: 'grid', gap: 8 }}>{renderActions()}</div>
      </div>
    </div>
  );
}
